import _ from 'lodash';

import Entity from './Entity';

import SurveyQuestion from './SurveyQuestion';
import {
  Facility,
  FacilitySettings
} from './Facility';
import Rate from './Rate';
import Fee from './Fee';

import { dayOfTheMonthFieldOptions } from './util';

import {
  queryGetFacilitySettings
} from '../Queries';


const billingProfileEntityFieldsWithDynamicAllowedValues = {
  billing_timing: { enumName: 'BillingTimingEnum' },
  parking_period: { enumName: 'ParkingPeriodEnum' },
  decline_recurring: { enumName: 'DeclineRecurringEnum' }
};

const billingProfileEntityFieldsWithStaticAllowedValues = {
  active: {
    options: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' }
    ]
  },
  validation_only: {
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' }
    ]
  },
  billing_day: {
    options: {
      'CALENDAR_DAY': dayOfTheMonthFieldOptions,
      'ROLLING_PERIOD': [
        { value: 0, label: '0' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 10, label: '10' },
        { value: 11, label: '11' },
        { value: 12, label: '12' },
        { value: 13, label: '13' },
        { value: 14, label: '14' },
        { value: 15, label: '15' },
        { value: 16, label: '16' },
        { value: 17, label: '17' },
        { value: 18, label: '18' },
        { value: 19, label: '19' },
        { value: 20, label: '20' }
      ]
    }
  },
  payment_due_day: {
    options: dayOfTheMonthFieldOptions
  },
  mail_invoice_option: {
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' }
    ]
  },
  termination_notice_day: {
    options: {
      'CALENDAR_DAY': dayOfTheMonthFieldOptions,
      'ROLLING_PERIOD': [
        { value: 0, label: '0' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 10, label: '10' },
        { value: 11, label: '11' },
        { value: 12, label: '12' },
        { value: 13, label: '13' },
        { value: 14, label: '14' },
        { value: 15, label: '15' },
        { value: 16, label: '16' },
        { value: 17, label: '17' },
        { value: 18, label: '18' },
        { value: 19, label: '19' },
        { value: 20, label: '20' },
        { value: 21, label: '21' },
        { value: 22, label: '22' },
        { value: 23, label: '23' },
        { value: 24, label: '24' },
        { value: 25, label: '25' },
        { value: 26, label: '26' },
        { value: 27, label: '27' },
        { value: 28, label: '28' },
        { value: 29, label: '29' },
        { value: 30, label: '30' },
        { value: 31, label: '31' }
      ]
    }
  },
  provide_survey: {
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' }
    ]
  }
};


const facilitySettingsQueryFields = [
  'decline_recurring',
  'billing_timing',
  'billing_day',
  'payment_due_day',
  'mail_invoice_option',
  'termination_notice_day',
  'provide_survey',
  'next_steps',
  { 'fees': [
    'type',
    'price',
    'tax_1',
    'tax_2',
    'tax_3',
    'tax_4',
    'gross_price',
    'start_date'
  ] },
  { 'survey_questions': [
    'type',
    'structure',
    'order'
  ] }
];


export default class BillingProfile {
  fields = {
    id: {
      value: null
    },
    facility_id: {
      value: null
    },
    name: {
      value: null
    },
    active: {
      value: null
    },
    validation_only: {
      value: false
    },
    billing_timing: {
      value: null
    },
    billing_day: {
      value: null
    },
    payment_due_day: {
      value: null
    },
    next_steps: {
      value: null
    },
    parking_period: {
      value: null
    },
    mail_invoice_option: {
      value: null
    },
    termination_notice_day: {
      value: null
    },
    decline_recurring: {
      value: null
    },
    provide_survey: {
      value: null
    },
    survey_questions: {
      purchase: {
        value: []
      },
      termination: {
        value: []
      }
    },
    facility: {
      value: null
    },
    rates: {
      value: []
    },
    fees: {
      mail_invoice: {
        value: []
      }
    }
  };


  static createFromQueryValues(values) {
    const billingProfile = new this();

    for (let field in billingProfile.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'survey_questions':
            if (Array.isArray(values[field])) {
              for (let type in billingProfile.fields[field]) {
                const surveyQuestionsOfType = values[field].filter(e => e.type === type.toUpperCase());
                if (surveyQuestionsOfType.length) {
                  billingProfile.fields[field][type].value = [];
                  for (let surveyQuestion of surveyQuestionsOfType) {
                    billingProfile.fields[field][type].value.push(SurveyQuestion.createFromQueryValues(surveyQuestion));
                  }
                }
              }
            }
            break;
          case 'facility':
            if (values[field] !== null) {
              billingProfile.fields[field].value = Facility.createFromQueryValues(values[field]);
            }
            break;
          case 'rates':
            if (Array.isArray(values[field])) {
              for (let rate of values[field]) {
                billingProfile.fields[field].value.push(Rate.createFromQueryValues(rate));
              }
            }
            break;
          case 'fees':
            if (Array.isArray(values[field])) {
              for (let type in billingProfile.fields[field]) {
                const feesOfType = values[field].filter(e => e.type === type.toUpperCase());
                if (feesOfType.length) {
                  billingProfile.fields[field][type].value = [];
                  for (let fee of feesOfType) {
                    billingProfile.fields[field][type].value.push(Fee.createFromQueryValues(fee));
                  }
                }
              }
            }
            break;
          default:
            billingProfile.fields[field].value = values[field];
        }
      }
    }

    return billingProfile;
  }


  static createFromDefaultValuesForFacility(facilityId) {
    return new Promise((resolve, reject) => {
      queryGetFacilitySettings({
        facility_id: facilityId
      }, facilitySettingsQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const billingProfile = new this();

        if (response.data.getFacilitySettings) {
          const facilitySettings = FacilitySettings.createFromQueryValues(response.data.getFacilitySettings);

          for (let field in facilitySettings.fields) {
            if (
              facilitySettings.fields[field].value !== null &&
              billingProfile.fields.hasOwnProperty(field)
            ) {
              switch (field) {
                case 'fees':
                case 'survey_questions':
                  for (let elementType in facilitySettings.fields[field]) {
                    if (!Object.keys(billingProfile.fields[field]).includes(elementType)) continue;

                    billingProfile.fields[field][elementType].value = facilitySettings.fields[field][elementType].value;
                  }
                  break;
                default:
                  billingProfile.fields[field].value = facilitySettings.fields[field].value;
              }
            }
          }
        }

        resolve(billingProfile);
      }, error => {
        reject([error]);
      });
    });
  }


  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      billingProfileEntityFieldsWithStaticAllowedValues,
      billingProfileEntityFieldsWithDynamicAllowedValues,
      fields
    );
  }


  getQueryFieldValues() {
    const values = {
      billing_profile: _.cloneDeep(BillingProfileInput),
      survey_questions: [],
      fees: []
    };

    for (let field in values.billing_profile) {
      switch (field) {
        case 'id':
          if (this.fields[field].value === null) {
            delete values.billing_profile[field];
            values.facility_id = this.fields.facility_id.value;
          }
          else {
            values.billing_profile[field] = this.fields[field].value;
          }
          break;
        default:
          values.billing_profile[field] = (this.fields[field].value === '') ? null : this.fields[field].value;
      }
    }

    // Survey Questions
    for (let elementType in this.fields.survey_questions) {
      if (this.fields.survey_questions[elementType].value.length) {
        for (let surveyQuestion of this.fields.survey_questions[elementType].value) {
          const elementQueryFieldValues = surveyQuestion.getQueryFieldValues();
          if (elementQueryFieldValues) {
            values.survey_questions.push(elementQueryFieldValues);
          }
        }
      }
    }

    // Fees
    for (let elementType in this.fields.fees) {
      if (this.fields.fees[elementType].value.length) {
        for (let fee of this.fields.fees[elementType].value) {
          const elementQueryFieldValues = fee.getQueryFieldValues();
          if (elementQueryFieldValues) {
            values.fees.push(elementQueryFieldValues);
          }
        }
      }
    }

    return values;
  }
}


export const BillingProfileInput = {
  id: null,
  name: null,
  active: null,
  billing_timing: null,
  billing_day: null,
  payment_due_day: null,
  next_steps: null,
  parking_period: null,
  mail_invoice_option: null,
  termination_notice_day: null,
  decline_recurring: null,
  provide_survey: null,
  validation_only: false,
}
